import React, { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { User } from '@/types/model';
import {
  useAddCredits,
  useAddDemoCredits,
  useDeductCredits,
  useAddCreditsForSale,
  useDeductCreditsForSale, useAuth
} from '@/hooks';
import { Coins, ShoppingCart, CalendarClock } from 'lucide-react';
import { dateWithMonthName } from '@/utils/formatters.ts';

type CreditOperation =
  | 'ADD_CREDITS'
  | 'DEDUCT_CREDITS'
  | 'ADD_DEMO_CREDITS'
  | 'ADD_CREDITS_FOR_SALE'
  | 'DEDUCT_CREDITS_FOR_SALE';

interface CreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: Pick<User, 'id' | 'username' | 'fullName' | 'role' | 'credits' | 'saleCredits' | 'expirationDate'>;
}

const CreditsModal: React.FC<CreditsModalProps> = ({ isOpen, onClose, user }) => {
  const isTargetUserAgent = ['agent', 'superagent'].includes(user.role?.code || '');
  const { user: currentUser, refreshAuth } = useAuth();
  const [selectedOperation, setSelectedOperation] = useState<CreditOperation>('ADD_CREDITS');
  const [selectedPeriod, setSelectedPeriod] = useState<'months' | 'years'>(
    isTargetUserAgent ? 'years' : 'months'
  );
  const [amount, setAmount] = useState<string>('12');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const addCreditsMutation = useAddCredits();
  const addDemoCreditsMutation = useAddDemoCredits();
  const deductCreditsMutation = useDeductCredits();
  const addCreditsForSaleMutation = useAddCreditsForSale();
  const deductCreditsForSaleMutation = useDeductCreditsForSale();

  const getAvailableOperations = (): Array<{value: CreditOperation; label: string}> => {
    const baseOperations = [];

    if (isTargetUserAgent) {
      baseOperations.push(
        {
          value: 'ADD_CREDITS_FOR_SALE' as CreditOperation,
          label: 'Agregar Créditos para Venta'
        },
        {
          value: 'DEDUCT_CREDITS_FOR_SALE' as CreditOperation,
          label: 'Quitar Créditos para Venta'
        }
      );
    } else {
      baseOperations.push(
        {
          value: 'ADD_DEMO_CREDITS' as CreditOperation,
          label: 'Demo (No afecta créditos reales)'
        },
        {
          value: 'ADD_CREDITS' as CreditOperation,
          label: 'Añadir Créditos de App'
        }
      )

      if(['admin','superadmin'].includes(currentUser?.roleCode || '')) {
        baseOperations.push(
          {
            value: 'DEDUCT_CREDITS' as CreditOperation,
            label: 'Quitar Créditos de App'
          }
        );
      }
    }

    return baseOperations;
  };

  const getPeriodOptions = () => {
    if (isTargetUserAgent) {
      return [
        { value: 'months', label: 'Meses' },
        { value: 'years', label: 'Años' }
      ];
    }

    return [{ value: 'months', label: 'Meses' }];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const isDemoOperation = selectedOperation === 'ADD_DEMO_CREDITS';
    const isCurrentUserAgent = ['agent', 'superagent'].includes(currentUser?.roleCode || '');
    const demoCreditsAvailable = currentUser?.demoCreditsAvailable ?? 0;
    const parsedAmount = parseInt(amount);
    if (!isDemoOperation && (!amount || parsedAmount <= 0)) {
      setError('Por favor, introduce una cantidad válida');
      return;
    }
    if (isDemoOperation && isCurrentUserAgent && demoCreditsAvailable <= 0) {
      setError('No tienes créditos demo disponibles. Compra 30 créditos demo por 1 crédito de venta desde el panel.');
      return;
    }

    const normalizedAmount = isDemoOperation ? 1 : parsedAmount * (selectedPeriod === 'years' ? 12 : 1);
    const isDeductOperation = selectedOperation.includes('DEDUCT');
    const payloadReason = isDeductOperation && reason.trim() ? reason.trim() : undefined;

    const operationData = {
      targetUsername: user.username,
      amount: normalizedAmount,
      reason: payloadReason,
    };

    try {
      let shouldRefreshAuth = false;
      switch (selectedOperation) {
        case 'ADD_CREDITS':
          await addCreditsMutation.mutateAsync(operationData);
          break;
        case 'ADD_DEMO_CREDITS':
          await addDemoCreditsMutation.mutateAsync({
            userId: user.id,
            targetUsername: user.username,
          });
          shouldRefreshAuth = true;
          break;
        case 'DEDUCT_CREDITS':
          await deductCreditsMutation.mutateAsync(operationData);
          break;
        case 'ADD_CREDITS_FOR_SALE':
          await addCreditsForSaleMutation.mutateAsync(operationData);
          break;
        case 'DEDUCT_CREDITS_FOR_SALE':
          await deductCreditsForSaleMutation.mutateAsync(operationData);
          break;
      }

      setAmount('');
      setReason('');
      setSelectedOperation(isTargetUserAgent ? 'ADD_CREDITS_FOR_SALE' : 'ADD_CREDITS');
      setSelectedPeriod(isTargetUserAgent ? 'years' : 'months');
      onClose();
      if (shouldRefreshAuth) {
        void refreshAuth();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ocurrió un error');
    }
  };

  useEffect(() => {
    if(isTargetUserAgent) {
      setSelectedOperation('ADD_CREDITS_FOR_SALE');
      setSelectedPeriod('years');
    } else {
      setSelectedOperation('ADD_CREDITS');
      setSelectedPeriod('months');
    }
  }, [user, isOpen, isTargetUserAgent]);

  const isLoading =
    addCreditsMutation.isPending ||
    addDemoCreditsMutation.isPending ||
    deductCreditsMutation.isPending ||
    addCreditsForSaleMutation.isPending ||
    deductCreditsForSaleMutation.isPending;

  const calculateNewExpirationDate = (currentDate: string | undefined, addedCredits: string) => {
    if (!currentDate) {
      currentDate = new Date().toISOString();
    }
    const current = new Date(currentDate);
    const added = parseInt(addedCredits) || 0;
    current.setMonth(current.getMonth() + added);
    return current.toISOString();
  }

  const normalizedAmount = (parseInt(amount) || 0) * (selectedPeriod === 'years' ? 12 : 1);
  const isDemoOperation = selectedOperation === 'ADD_DEMO_CREDITS';
  const shouldShowReason = selectedOperation.includes('DEDUCT');
  const isDeductOperation = selectedOperation.includes('DEDUCT');
  const isCurrentUserAgent = ['agent', 'superagent'].includes(currentUser?.roleCode || '');
  const demoCreditsAvailable = currentUser?.demoCreditsAvailable ?? 0;
  const demoDays = 1;
  const calculateDemoExpirationDate = () => {
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + demoDays);
    return expiration.toISOString();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gestión de Créditos"
      size="lg"
      restoreFocusOnClose={false}
    >
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div>
              <div className="flex space-x-6 items-center">
                <div className="font-medium text-gray-900">{user.fullName} • <span className="text-sm font-normal text-gray-600">{user.username}</span></div>
                {!isTargetUserAgent && (
                  <>
                    <div className="flex space-x-1 items-center">
                      <Coins className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-600">Créditos:</span>
                      <span className="font-medium text-gray-900">{user.credits || 0}</span>
                    </div>
                    <div className="flex space-x-1 items-center">
                      <CalendarClock className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-gray-600">Vence:</span>
                      <span className="font-medium text-gray-900">
                        {dateWithMonthName(user.expirationDate, 'No disponible')}
                      </span>
                    </div>
                  </>)}
                {isTargetUserAgent && (
                  <div className="flex items-center space-x-2">
                    <ShoppingCart className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-600">Créditos para Venta:</span>
                    <span className="font-medium text-gray-900">{user.saleCredits || 0}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={isDemoOperation ? 'md:col-span-3' : 'md:col-span-2'}>
              <Select
                label="Operación"
                value={selectedOperation}
                onChange={(e) => setSelectedOperation(e.target.value as CreditOperation)}
                options={getAvailableOperations()}
                placeholder="Selecciona una operación"
              />
            </div>
            {!isDemoOperation && (
              <div className="md:col-span-1">
                <Select
                  label="Periodo"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value as 'months' | 'years')}
                  options={getPeriodOptions()}
                />
              </div>
            )}
          </div>

          {!isDemoOperation && (
            <Input
              label="Cantidad"
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Introduce la cantidad de créditos"
              required
            />
          )}

          {shouldShowReason && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                rows={1}
                placeholder="Indique la razon por la cual se le van a elimiar créditos..."
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{reason.length}/500 caracteres</p>
            </div>
          )}

          {!isDemoOperation && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <div>
                  <h3 className="mb-3 font-medium text-sm text-gray-900">Se procederá a {selectedOperation.includes('ADD') ? 'vender' : 'Quitar'} {normalizedAmount} créditos a {user.fullName}</h3>
                  <div className="flex space-x-6 items-center">
                    {!isTargetUserAgent && (
                      <>
                        <div className="flex space-x-1 items-center">
                          <Coins className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-gray-600">Nuevo Balance:</span>
                          {!isDeductOperation && <span className="font-medium text-gray-900">{(user.credits || 0)+ normalizedAmount}</span>}
                          {isDeductOperation && <span className="font-medium text-gray-900">{Math.max((user.credits || 0) - normalizedAmount, 0)}</span>}
                        </div>
                        <div className="flex space-x-1 items-center">
                          <CalendarClock className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm text-gray-600">Nueva Fecha:</span>
                          <span className="font-medium text-gray-900">
                          {dateWithMonthName(calculateNewExpirationDate(user.expirationDate, normalizedAmount.toString()), 'No disponible')}
                        </span>
                        </div>
                      </>)}
                    {isTargetUserAgent && (
                      <div className="flex items-center space-x-2">
                        <Coins className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-600">Nuevo Balance:</span>
                        {!isDeductOperation && <span className="font-medium text-gray-900">{(user.saleCredits || 0) + normalizedAmount}</span>}
                        {isDeductOperation && <span className="font-medium text-gray-900">{Math.max((user.saleCredits || 0) - normalizedAmount, 0)}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {isDemoOperation && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <h3 className="font-medium text-sm text-gray-900">
                  Se otorgará un demo de {demoDays} día{demoDays > 1 ? 's' : ''} a {user.fullName}
                </h3>
                <div className="flex items-center space-x-2">
                  <CalendarClock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-gray-600">Fecha de vencimiento:</span>
                  <span className="font-medium text-gray-900">
                    {dateWithMonthName(calculateDemoExpirationDate(), 'No disponible')}
                  </span>
                </div>
                {isCurrentUserAgent && (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Créditos demo disponibles:</span>
                      <span className="font-medium text-gray-900">
                        {demoCreditsAvailable}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Cada demo consume 1 crédito demo y habilita 1 día. Cuando llegues a 0, podrás comprar 30 créditos demo por 1 crédito de venta.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading || (isDemoOperation && isCurrentUserAgent && demoCreditsAvailable <= 0)}
            >
              {isDemoOperation ? 'Otorgar Demo' : `${selectedOperation.includes('ADD') ? 'Añadir' : 'Quitar'} Créditos`}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreditsModal;
